<template>
  <v-form ref="form" :disabled="$loader.loading || readonly">
    <v-row>
      <v-col cols="4">
        <div class="d-flex">
          <v-img :src="imagePreview" max-height="170" />
        </div>
      </v-col>
      <v-col cols="8">
        <v-row>
          <v-col cols="4">
            <v-text-field
              v-model="model.data.trade_name"
              variant="outlined"
              label="Nome Fantasia *"
              :rules="[rules.required]"
              required
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="model.data.company_name"
              variant="outlined"
              label="Nome / Razão Social *"
              :rules="[rules.required]"
              required
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="model.data.display_name"
              variant="outlined"
              label="Nome de Exibição *"
              :rules="[rules.required]"
              required
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-maska="$masks.cnpj.opts.mask"
              :model-value="model.data.tax_id"
              variant="outlined"
              label="CNPJ *"
              :rules="[rules.required, rules.cnpj]"
              required
              placeholder="00.000.000/0000-00"
              @update:model-value="(v: string) => model.data.tax_id = $masks.cnpj.unmasked(v)"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-maska="$masks.cellphone.opts.mask"
              :model-value="model.data.cellphone"
              variant="outlined"
              label="Telefone *"
              @update:model-value="(v: string) => model.data.cellphone= $masks.cellphone.unmasked(v)"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="4">
        <v-file-input
          ref="logo"
          v-model="model.data.logo"
          label="Logotipo (150x150px, formato PNG)"
          accept="image/*"
          outlined
          @change="onFileChange"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="model.data.website_url"
          variant="outlined"
          label="Website"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="model.data.ticket_advantages_url"
          variant="outlined"
          label="URL Ingresso de Vantagens"
          :readonly="readonly"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="3">
        <v-text-field
          v-maska="$masks.cep.opts.mask"
          :model-value="model.data.address.zipcode"
          variant="outlined"
          label="CEP *"
          :rules="[rules.required]"
          required
          @update:model-value="(v: string) => onCepChange(v)"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="model.data.address.state"
          variant="outlined"
          label="Estado *"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="model.data.address.city"
          variant="outlined"
          label="Cidade *"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="model.data.address.neighborhood"
          variant="outlined"
          label="Bairro *"
          :rules="[rules.required]"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="4">
        <v-text-field
          v-model="model.data.address.street"
          variant="outlined"
          label="Endereço *"
          :rules="[rules.required]"
          required
        />
      </v-col>
      <v-col cols="2">
        <v-text-field
          v-model="model.data.address.number"
          variant="outlined"
          label="Número"
          prefix="N°"
        />
      </v-col>
      <v-col cols="6">
        <v-text-field v-model="model.data.address.complement" variant="outlined" label="Complemento" />
      </v-col>
    </v-row>

    <h3 class="my-4 mb-1">
      Redes sociais
    </h3>
    <v-row>
      <template v-if="model.additional.social">
        <v-col cols="4">
          <v-text-field
            v-model="model.additional.social.facebook_url"
            variant="outlined"
            label="Facebook"
            prefix="https://facebook.com/"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model="model.additional.social.twitter_url"
            variant="outlined"
            label="X (Twitter)"
            prefix="https://x.com/"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model="model.additional.social.instagram_url"
            variant="outlined"
            label="Instagram"
            prefix="http://www.instagram.com/"
          />
        </v-col>
      </template>
    </v-row>

    <h3 class="my-4 mb-1">
      Administrativo
    </h3>
    <v-row>
      <v-col cols="12">
        <v-text-field v-model="model.additional.administrative.name" variant="outlined" label="Nome *" />
      </v-col>
      <v-col cols="6">
        <v-text-field v-model="model.additional.administrative.email" variant="outlined" label="Email *" />
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-maska="$masks.cellphone.opts.mask"
          :model-value="model.additional.administrative.cellphone"
          variant="outlined"
          label="Telefone *"
          @update:model-value="(v: string) => model.additional.administrative.cellphone = $masks.cellphone.unmasked(v)"
        />
      </v-col>
    </v-row>

    <h3 class="my-4 mb-1">
      Financeiro
    </h3>
    <v-row>
      <v-col cols="12">
        <v-text-field v-model="model.additional.financial.name" variant="outlined" label="Nome *" />
      </v-col>
      <v-col cols="6">
        <v-text-field v-model="model.additional.financial.email" variant="outlined" label="Email *" />
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-maska="$masks.cellphone.opts.mask"
          :model-value="model.additional.financial.cellphone"
          variant="outlined"
          label="Telefone *"
          @update:model-value="(v: string) => model.additional.financial.cellphone = $masks.cellphone.unmasked(v)"
        />
      </v-col>
    </v-row>
    <h3 class="my-4">
      Descrição Institucional
    </h3>
    <v-row id="dtk-text-editor">
      <v-col cols="12">
        <text-editor
          v-model="model.data.description"
          placeholder="Insira uma descrição..."
          :loading="loading"
          :readonly="readonly"
        />
      </v-col>
    </v-row>

    <v-row v-if="!readonly" justify="end" class="mt-1">
      <v-col cols="2">
        <v-btn
          color="primary"
          block
          :loading="$loader.loading"
          @click="submit"
        >
          Salvar
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import ViaCEPService from '@/services/ViaCEPService'
import validateCNPJ from '@/helpers/rules/validateCNPJ'
import type Producer from '@/types/Producer'
import TextEditor from '@/components/text-editor/TextEditor.vue'

export default defineComponent({
  components: {
    TextEditor,
  },
  props: {
    producer: {
      type: Object as PropType<Producer>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  emits: [ 'submit' ],
  data() {
    return {
      image: undefined as File | undefined,
      valid: true,
      model: this.producer,
      isUserInput: false,
      rules: {
        required: (value: string) => !!value || 'Este campo é obrigatório',
        cnpj: validateCNPJ,
      },
      timeout: {
        cep: null,
      } as Record<string, number | null>,
    }
  },
  computed: {
    imagePreview() {
      // If the image was just attached
      const files = (this.$refs.logo as HTMLInputElement)?.files ?? []
      if (files.length > 0) return URL.createObjectURL(files[0])

      // If the image came from the API with the producer
      const logo = this.producer.images?.find(img => img.tag === 'producer-logo')
      if (logo) return logo.url

      // No image attached, usually only when creating a producer.
      if (!this.image) return '/src/assets/images/producer_placeholder.png'
      return undefined
    },
    loading(): boolean {
      return this.$loader.loading
    },
  },
  watch: {
    producer(_new) {
      this.model = _new
    },
    'model.data.address.zipcode'(newCep) {
      this.isUserInput = true
      this.onCepChange(newCep)
    },
  },
  methods: {
    onFileChange(event: Event) {
      const files = (event.target as HTMLInputElement).files
      if (files && files.length > 0) {
        this.image = files[0]
        this.model.data.logo = files[0]
      }
    },
    submit() {
      this.$emit('submit', this.model)
    },
    onCepChange(value: string) {
      if (!this.$masks.cep.completed(value)) return
      if (this.timeout.cep) clearTimeout(this.timeout.cep)

      this.model.data.address.zipcode = this.$masks.cep.unmasked(value)
      if (this.isUserInput) {
        this.timeout.cep = setTimeout(this.fetchAddress, 1000)
      }
    },
    async fetchAddress() {
      try {
        const { data } = await (new ViaCEPService()).search(this.model.data.address.zipcode)

        this.model.data.address.state = data.uf
        this.model.data.address.city = data.localidade
        this.model.data.address.neighborhood = data.bairro
        this.model.data.address.street = data.logradouro
        this.model.data.address.complement = data.complemento
      } catch {
        this.$snackbar.show('CEP não encontrado', 'error')
      }
    },
  },
})
</script>
